import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Users, CalendarDays, Receipt, AlertTriangle,
  Wallet, Building2, ArrowRight, Clock
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color, link }) => (
  <Link to={link || '#'} className="stat-card group" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs tracking-[0.05em] uppercase font-semibold text-[#4F734F] mb-2">{label}</p>
        <p className="font-heading text-3xl font-light tracking-tight text-[#111B11]">{value}</p>
      </div>
      <div className={`p-2.5 rounded-sm ${color}`}>
        <Icon size={20} strokeWidth={1.5} />
      </div>
    </div>
    <div className="flex items-center gap-1 mt-4 text-xs text-olive-500 group-hover:text-[#D4AF37] transition-colors">
      <span>View details</span>
      <ArrowRight size={12} />
    </div>
  </Link>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const { data: summary } = await api.get('/dashboard/summary');
        setData(summary);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const d = data || {};

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">
          Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.name}
        </h1>
        <p className="text-sm text-[#4F734F] mt-1">Here's what's happening at SVT & Co today</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <StatCard icon={Users} label="Total Clients" value={d.total_clients || 0} color="bg-olive-100 text-olive-700" link="/clients" />
        <StatCard icon={CalendarDays} label="Today's Appointments" value={d.today_appointments?.length || 0} color="bg-blue-50 text-blue-600" link="/appointments" />
        <StatCard icon={Receipt} label="Pending GST Filings" value={d.pending_gst_filings || 0} color="bg-yellow-50 text-yellow-600" link="/gst-pt" />
        <StatCard icon={AlertTriangle} label="Overdue Documents" value={d.overdue_documents || 0} color="bg-red-50 text-red-600" link="/documents" />
        <StatCard icon={Wallet} label="Petty Cash Balance" value={`${(d.petty_cash_balance || 0).toLocaleString('en-IN')}`} color="bg-green-50 text-green-600" link="/petty-cash" />
        <StatCard icon={Building2} label="Rent Dues" value={d.rent_dues || 0} color="bg-purple-50 text-purple-600" link="/rent-tracker" />
        <StatCard icon={Receipt} label="Pending PT Filings" value={d.pending_pt_filings || 0} color="bg-orange-50 text-orange-600" link="/gst-pt" />
      </div>

      {/* Client Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-[#E6EDE6] rounded-sm p-6" data-testid="client-types-card">
          <h2 className="font-heading text-lg font-medium text-[#213021] mb-4">Clients by Type</h2>
          <div className="space-y-3">
            {[
              { label: 'Income Tax', count: d.client_counts?.income_tax || 0, color: 'bg-olive-500' },
              { label: 'GST & PT', count: d.client_counts?.gst_pt || 0, color: 'bg-[#D4AF37]' },
              { label: 'Business', count: d.client_counts?.business || 0, color: 'bg-olive-700' },
              { label: 'Consulting', count: d.client_counts?.consulting || 0, color: 'bg-olive-400' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                  <span className="text-sm text-[#3B5A3B]">{item.label}</span>
                </div>
                <span className="font-heading text-lg font-medium text-[#111B11]">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Today's Appointments */}
        <div className="bg-white border border-[#E6EDE6] rounded-sm p-6" data-testid="today-appointments-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-medium text-[#213021]">Today's Appointments</h2>
            <Link to="/appointments" className="text-xs text-[#D4AF37] hover:underline">View all</Link>
          </div>
          {d.today_appointments?.length > 0 ? (
            <div className="space-y-3">
              {d.today_appointments.map((appt, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-sm bg-[#F4F7F4]">
                  <div className="p-1.5 rounded-sm bg-olive-100">
                    <Clock size={14} className="text-olive-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#213021] truncate">{appt.client_name}</p>
                    <p className="text-xs text-olive-500">{appt.time} - {appt.reason}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    appt.status === 'Completed' ? 'bg-green-100 text-green-700' :
                    appt.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {appt.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-olive-400 text-center py-8">No appointments scheduled for today</p>
          )}
        </div>
      </div>
    </div>
  );
}
